import React, {
  forwardRef,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronLeft, ChevronRight } from '@bloomberg/media-ui-icons';

import { useSlug } from '../hooks/useSlug';
import defaultMessages from '../i18n/defaultMessages';
import { IconButton } from '../IconButton';
import { GlobalProps } from '../types';
import { createClasses } from '../utils';
import { useTranslateMessage } from '../utils/translate-messages';

import styles from './Carousel.module.css';

const CAROUSEL_LIMIT = 6;
const MIN_SWIPE_DISTANCE = 50;

type DotProps = {
  onClick: (index: number) => void;
  index: number;
  isActive: boolean;
  ariaLabel: string;
};

function filterKeyEnter(handler: (event: KeyboardEvent) => void) {
  return (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handler(e);
    }
  };
}

export function accessibleOnClick(handler: () => void) {
  return {
    role: 'button',
    onKeyDown: filterKeyEnter(handler),
    tabIndex: 0,
    onClick: handler,
  };
}

function Dot({ onClick, index, isActive, ariaLabel }: DotProps) {
  const handleOnClick = useCallback(() => {
    onClick(index);
  }, [onClick, index]);

  return (
    <span
      className={createClasses([styles.dot, isActive && styles.activeDot])}
      aria-label={ariaLabel}
      {...accessibleOnClick(handleOnClick)}
    />
  );
}

export type NavButtonIconProps = {
  direction: string;
  isActive: boolean;
};

function NavButtonIcon({ direction, isActive }: NavButtonIconProps) {
  const navButton = createClasses([styles.navButtonContainer]);

  return (
    <div>
      <IconButton
        variant="outlined"
        size="default"
        className={navButton}
        disabled={!isActive}
      >
        {direction === 'left' ? <ChevronLeft /> : <ChevronRight />}
      </IconButton>
    </div>
  );
}

export type CarouselNavProps = GlobalProps & {
  children: React.ReactNode[];
  limit?: number;
  autoadvanceIntervalInMs?: null | number;
  animatedTransition?: boolean;
  trackingName?: string;
  trackClick?: (eventAction: string, eventLabel: string) => void;
};

export const Carousel = forwardRef<HTMLInputElement, CarouselNavProps>(
  function Carousel(props: CarouselNavProps, ref) {
    const {
      children,
      limit = CAROUSEL_LIMIT,
      autoadvanceIntervalInMs = null,
      animatedTransition = false,
      trackingName = 'carousel',
      trackClick,
      name = 'carousel',
    } = props;

    const componentName = useSlug(name);
    const translateMessage = useTranslateMessage();

    const navigationDotLabel = translateMessage({
      id: 'media-ui-core_navigationDot',
      defaultMessage: defaultMessages['media-ui-core_navigationDot'],
    });
    const navigateLeftLabel = translateMessage({
      id: 'media-ui-core_navigateLeft',
      defaultMessage: defaultMessages['media-ui-core_navigateLeft'],
    });
    const navigateRightLabel = translateMessage({
      id: 'media-ui-core_navigateRight',
      defaultMessage: defaultMessages['media-ui-core_navigateRight'],
    });

    const autoadvancing = useRef<NodeJS.Timeout | null>(null);
    const [currentCard, setCurrentCard] = useState(0);
    const touchStart = useRef<number | null>(null);
    const touchEnd = useRef<number | null>(null);
    const navSectionButtonsClass = createClasses([
      styles.navSection,
      styles.navSectionButtons,
    ]);

    // max items slice it
    const items = useMemo(
      () => children?.slice(0, limit + 1),
      [children, limit]
    );

    const autoadvance = useCallback((interval: number, cardCount: number) => {
      autoadvancing.current = setInterval(() => {
        setCurrentCard((prevCard) => (prevCard + 1) % cardCount);
      }, interval);
    }, []);

    const stopAutoadvance = useCallback(() => {
      if (autoadvancing.current) {
        clearInterval(autoadvancing.current);
        autoadvancing.current = null;
      }
    }, []);

    const resetAutoAdvance = useCallback(() => {
      stopAutoadvance();

      if (autoadvanceIntervalInMs) {
        autoadvance(autoadvanceIntervalInMs, items?.length);
      }
    }, [autoadvanceIntervalInMs, items?.length, autoadvance, stopAutoadvance]);

    useEffect(() => {
      if (autoadvanceIntervalInMs) {
        autoadvance(autoadvanceIntervalInMs, items?.length);
      }

      return stopAutoadvance;
    }, [autoadvanceIntervalInMs, items?.length, autoadvance, stopAutoadvance]);

    const navigateLeft = useCallback(() => {
      setCurrentCard((prevCard) => Math.max(prevCard - 1, 0));

      resetAutoAdvance();
      if (trackClick) {
        trackClick('arrow-click', `${trackingName}.arrow-back`);
      }
    }, [resetAutoAdvance]);

    const navigateRight = useCallback(() => {
      setCurrentCard((prevCard) =>
        Math.min(prevCard + 1, (items?.length || 0) - 1)
      );

      resetAutoAdvance();
      if (trackClick) {
        trackClick('arrow-click', `${trackingName}.arrow-forward`);
      }
    }, [resetAutoAdvance, items?.length]);

    const navigateToCard = useCallback(
      (nextCurrentCard: number) => {
        setCurrentCard(nextCurrentCard);

        resetAutoAdvance();

        if (trackClick) {
          trackClick(
            'arrow-click',
            `${trackingName}.card.${nextCurrentCard + 1}`
          );
        }
      },
      [resetAutoAdvance]
    );

    const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
      touchEnd.current = null;
      touchStart.current = e.targetTouches[0]?.clientX || null;
    };

    const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
      touchEnd.current = e.targetTouches[0]?.clientX || null;
    };

    const onTouchEnd = () => {
      if (touchStart.current === null || touchEnd.current === null) return;
      const distance = touchEnd.current - touchStart.current;
      const swipeLeftToRight = distance > MIN_SWIPE_DISTANCE;
      const swipeRightToLeft = distance < -MIN_SWIPE_DISTANCE;

      if (swipeLeftToRight) {
        navigateLeft();
      }

      if (swipeRightToLeft) {
        navigateRight();
      }
    };

    const isAtStart = currentCard === 0;
    const isAtEnd = currentCard === (items?.length || 0) - 1;

    if (!items?.length) {
      return <></>;
    }

    if (animatedTransition) {
      return (
        <div data-component={componentName} ref={ref}>
          <div
            className={styles.animatedBox}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {items.map((item: React.ReactNode, index: number) => {
              const cx = createClasses([
                styles.animatedCard,
                index < currentCard && styles.prevCard,
                index > currentCard && styles.nextCard,
              ]);

              return (
                <span className={cx} key={index}>
                  {item}
                </span>
              );
            })}
          </div>
          <div className={styles.carouselNav} data-component="carousel-nav">
            <div className={styles.navSection} />
            <div className={styles.navSection}>
              {items.map((_, index: number) => (
                <Dot
                  key={index}
                  index={index}
                  isActive={index === currentCard}
                  onClick={navigateToCard}
                  ariaLabel={navigationDotLabel}
                />
              ))}
            </div>
            <div className={navSectionButtonsClass}>
              <button
                onClick={navigateLeft}
                disabled={isAtStart}
                aria-label={navigateLeftLabel}
              >
                <NavButtonIcon direction="left" isActive={!isAtStart} />
              </button>
              <button
                onClick={navigateRight}
                disabled={isAtEnd}
                aria-label={navigateRightLabel}
              >
                <NavButtonIcon direction="right" isActive={!isAtEnd} />
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div data-component={componentName} ref={ref}>
        {items[currentCard]}
        <div className={styles.carouselNav} data-component="carousel-nav">
          <div className={styles.navSection} />
          <div className={styles.navSection}>
            {items.map((_, index: number) => (
              <Dot
                key={index}
                isActive={index === currentCard}
                onClick={setCurrentCard}
                index={index}
                ariaLabel={navigationDotLabel}
              />
            ))}
          </div>
          <div className={navSectionButtonsClass}>
            <button
              onClick={navigateLeft}
              disabled={isAtStart}
              aria-label={navigateLeftLabel}
            >
              <NavButtonIcon direction="left" isActive={!isAtStart} />
            </button>
            <button
              onClick={navigateRight}
              disabled={isAtEnd}
              aria-label={navigateRightLabel}
            >
              <NavButtonIcon direction="right" isActive={!isAtEnd} />
            </button>
          </div>
        </div>
      </div>
    );
  }
);
